import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { isEqual } from "lodash";
import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { TopicData, useVizDispatch } from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  VectorField,
  VectorFieldDisplayMode,
} from "@/shared/state/visualization/schema/v1/three-d";

import { DataSourceSelector } from "../../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../../Panel/predicates";
import { ConfigItem } from "../ConfigItem";

import styles from "./EntityConfig.module.css";
import {
  getFileAndPath,
  topicDataFor,
} from "./PositionConfig/positionTopicData";

interface Props {
  entity: Entity;
  panelId: string;
}

export function VectorFieldConfig({ entity, panelId }: Props) {
  const dispatch = useVizDispatch();

  // Track the most recent vectorField, inclusive of updates originating from this component.
  const vectorFieldRef = React.useRef(entity.vectorField);

  // A key used to reload the data source selectors to match external updates.
  const reloadKeyRef = React.useRef(0);

  // Data source state
  const [forwardData, setForwardData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.forward),
  );
  const [rightData, setRightData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.right),
  );
  const [upData, setUpData] = React.useState<TopicData | undefined>(
    topicDataFor(entity.vectorField?.up),
  );

  // Scale state
  const [forwardScale, setForwardScale] = React.useState(
    `${entity.vectorField?.forwardScale ?? 1}`,
  );
  const [forwardScaleError, setForwardScaleError] = React.useState("");
  const [rightScale, setRightScale] = React.useState(
    `${entity.vectorField?.rightScale ?? 1}`,
  );
  const [rightScaleError, setRightScaleError] = React.useState("");
  const [upScale, setUpScale] = React.useState(
    `${entity.vectorField?.upScale ?? 1}`,
  );
  const [upScaleError, setUpScaleError] = React.useState("");

  // Display state
  const [lineWidth, setLineWidth] = React.useState(
    `${entity.vectorField?.lineWidth ?? 2}`,
  );
  const [lineWidthError, setLineWidthError] = React.useState("");
  const [color, setColor] = React.useState(
    entity.vectorField?.color || "#e85d3a",
  );
  const [stride, setStride] = React.useState(
    `${entity.vectorField?.stride ?? 1}`,
  );
  const [strideError, setStrideError] = React.useState("");
  const [displayMode, setDisplayMode] = React.useState<VectorFieldDisplayMode>(
    entity.vectorField?.displayMode ?? "both",
  );
  const [gradientEnabled, setGradientEnabled] = React.useState(
    entity.vectorField?.gradientEnabled ?? false,
  );
  const [colorLow, setColorLow] = React.useState(
    entity.vectorField?.colorLow || "#0000ff",
  );
  const [colorHigh, setColorHigh] = React.useState(
    entity.vectorField?.colorHigh || "#ff0000",
  );

  const forwardConfig = getFileAndPath(entity.vectorField?.forward);
  const rightConfig = getFileAndPath(entity.vectorField?.right);
  const upConfig = getFileAndPath(entity.vectorField?.up);

  const parseNumberInput = (
    value: string,
    label: string,
  ):
    | { ok: true; value: number; error?: undefined }
    | { ok: false; error: string } => {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return { ok: true, value: parsed };
    }
    return { ok: false, error: `${label} must be a number.` };
  };

  React.useEffect(
    function onFieldsChanged() {
      if (!isEqual(vectorFieldRef.current, entity.vectorField)) {
        // entity.vectorField was changed without this component's involvement.
        vectorFieldRef.current = entity.vectorField;
        setForwardScale(`${entity.vectorField?.forwardScale ?? ""}`);
        setRightScale(`${entity.vectorField?.rightScale ?? ""}`);
        setUpScale(`${entity.vectorField?.upScale ?? ""}`);
        setLineWidth(`${entity.vectorField?.lineWidth ?? ""}`);
        setColor(entity.vectorField?.color || "#e85d3a");
        setStride(`${entity.vectorField?.stride ?? ""}`);
        setDisplayMode(entity.vectorField?.displayMode ?? "both");
        setGradientEnabled(entity.vectorField?.gradientEnabled ?? false);
        setColorLow(entity.vectorField?.colorLow || "#0000ff");
        setColorHigh(entity.vectorField?.colorHigh || "#ff0000");

        setForwardData(topicDataFor(entity.vectorField?.forward));
        setRightData(topicDataFor(entity.vectorField?.right));
        setUpData(topicDataFor(entity.vectorField?.up));
        reloadKeyRef.current++;
        return;
      }

      // Require at least one data source to be set before dispatching.
      if (!forwardData && !rightData && !upData) {
        return;
      }

      const parsedForwardScale = parseNumberInput(
        forwardScale,
        "Forward scale",
      );
      const parsedRightScale = parseNumberInput(rightScale, "Right scale");
      const parsedUpScale = parseNumberInput(upScale, "Up scale");
      const parsedLineWidth = parseNumberInput(lineWidth, "Line width");
      const parsedStride = parseNumberInput(stride, "Stride");

      setForwardScaleError(parsedForwardScale.error ?? "");
      setRightScaleError(parsedRightScale.error ?? "");
      setUpScaleError(parsedUpScale.error ?? "");
      setLineWidthError(parsedLineWidth.error ?? "");
      setStrideError(parsedStride.error ?? "");

      if (
        !parsedForwardScale.ok ||
        !parsedRightScale.ok ||
        !parsedUpScale.ok ||
        !parsedLineWidth.ok ||
        !parsedStride.ok
      ) {
        return;
      }

      if (parsedStride.value < 1) {
        setStrideError("Stride must be at least 1.");
        return;
      }

      const updated: VectorField = {
        forward: forwardData ?? entity.vectorField?.forward ?? 0,
        forwardScale: parsedForwardScale.value,
        right: rightData ?? entity.vectorField?.right ?? 0,
        rightScale: parsedRightScale.value,
        up: upData ?? entity.vectorField?.up ?? 0,
        upScale: parsedUpScale.value,
        color,
        lineWidth: parsedLineWidth.value,
        stride: parsedStride.value,
        displayMode,
        gradientEnabled,
        colorLow: gradientEnabled ? colorLow : undefined,
        colorHigh: gradientEnabled ? colorHigh : undefined,
      };

      if (!isEqual(updated, entity.vectorField)) {
        vectorFieldRef.current = updated;
        dispatch(
          updateThreeDEntity(panelId, entity.id, { vectorField: updated }),
        );
      }
    },
    [
      forwardData,
      rightData,
      upData,
      forwardScale,
      rightScale,
      upScale,
      lineWidth,
      color,
      stride,
      displayMode,
      gradientEnabled,
      colorLow,
      colorHigh,
      entity.vectorField,
      entity.id,
      panelId,
      dispatch,
    ],
  );

  return (
    <>
      <RobotoTypography variant="body2" className={styles.fieldLabel}>
        Vector Field
      </RobotoTypography>
      <div className={styles.container}>
        <div key={reloadKeyRef.current} className={styles.axisGroup}>
          <ConfigItem label="Right Axis">
            <DataSourceSelector
              initialFile={rightConfig?.fileId}
              initialPath={rightConfig?.path}
              onChange={(node) => setRightData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Forward Axis">
            <DataSourceSelector
              initialFile={forwardConfig?.fileId}
              initialPath={forwardConfig?.path}
              onChange={(node) => setForwardData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
          <ConfigItem label="Up Axis">
            <DataSourceSelector
              initialFile={upConfig?.fileId}
              initialPath={upConfig?.path}
              onChange={(node) => setUpData(node.toTopicData())}
              optionFilter={isViewableAsPlot}
            />
          </ConfigItem>
        </div>
        <ConfigItem label="Scaling">
          <div className={styles.fieldRow}>
            <TextField
              label="Right Scale"
              type="number"
              size="small"
              margin="dense"
              value={rightScale}
              error={!!rightScaleError}
              helperText={rightScaleError}
              onChange={(e) => setRightScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Forward Scale"
              type="number"
              size="small"
              margin="dense"
              value={forwardScale}
              error={!!forwardScaleError}
              helperText={forwardScaleError}
              onChange={(e) => setForwardScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Up Scale"
              type="number"
              size="small"
              margin="dense"
              value={upScale}
              error={!!upScaleError}
              helperText={upScaleError}
              onChange={(e) => setUpScale(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ step: 0.1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Display">
          <FormControl
            size="small"
            fullWidth
            className={`${styles.field} ${styles.displayControl}`}
          >
            <InputLabel id="vf-display-mode-label" className={styles.input}>
              Display Mode
            </InputLabel>
            <Select
              label="Display Mode"
              labelId="vf-display-mode-label"
              value={displayMode}
              onChange={(e) =>
                setDisplayMode(e.target.value as VectorFieldDisplayMode)
              }
              className={styles.input}
              inputProps={{ className: styles.input }}
            >
              <MenuItem value="both">Both current and field</MenuItem>
              <MenuItem value="field_only">Vector field only</MenuItem>
              <MenuItem value="current_only">Current vector only</MenuItem>
            </Select>
          </FormControl>
          <div className={styles.fieldRow}>
            <TextField
              label="Line Width"
              type="number"
              size="small"
              margin="dense"
              value={lineWidth}
              error={!!lineWidthError}
              helperText={lineWidthError}
              onChange={(e) => setLineWidth(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 0, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Color"
              type="color"
              size="small"
              margin="dense"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ className: styles.input }}
              className={styles.scaleField}
            />
            <TextField
              label="Stride"
              type="number"
              size="small"
              margin="dense"
              value={stride}
              error={!!strideError}
              helperText={strideError}
              onChange={(e) => setStride(e.target.value)}
              InputLabelProps={{ className: styles.input }}
              InputProps={{ className: styles.input }}
              inputProps={{ min: 1, step: 1, className: styles.input }}
              className={styles.scaleField}
            />
          </div>
        </ConfigItem>
        <ConfigItem label="Gradient">
          <div className={styles.fieldRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={gradientEnabled}
                  onChange={(e) => setGradientEnabled(e.target.checked)}
                  size="small"
                />
              }
              label="Enable gradient"
              className={styles.input}
            />
          </div>
          {gradientEnabled && (
            <div className={styles.fieldRow}>
              <TextField
                label="Low Color"
                type="color"
                size="small"
                margin="dense"
                value={colorLow}
                onChange={(e) => setColorLow(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
              <TextField
                label="High Color"
                type="color"
                size="small"
                margin="dense"
                value={colorHigh}
                onChange={(e) => setColorHigh(e.target.value)}
                InputLabelProps={{ className: styles.input }}
                InputProps={{ className: styles.input }}
                inputProps={{ className: styles.input }}
                className={styles.scaleField}
              />
            </div>
          )}
        </ConfigItem>
      </div>
    </>
  );
}
